/**
 * LinkedIn company-tag review queue.
 *
 * Companies whose LinkedIn tag the background resolver couldn't finish (0/>1
 * org URNs on the page, a name mismatch, or a priority company with no
 * candidate URL). The operator supplies/corrects the URL and confirms the exact
 * org URN. The exact URN is REQUIRED — a wrong one silently tags the wrong
 * company — so verifying is a human gate.
 *
 * Lives under the Review Queue (the operator's main review point). Writes go
 * through /admin/entities/:id/social, so a confirmation here also settles the
 * entity's per-entity social card.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listLinkedinReviewQueue,
  updateEntitySocial,
  type LinkedInReviewQueueItem,
} from '../lib/api/social.js';
import { pageNameCorroboratesEntity } from '../lib/linkedinCorroboration.js';
import { LinkedInUrnHelper } from './LinkedInUrnHelper.js';

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  needs_review: { cls: 'bg-amber-100 text-amber-700', label: 'needs review' },
  pending: { cls: 'bg-grey-100 text-grey-600', label: 'queued' },
  resolving: { cls: 'bg-blue-100 text-blue-700', label: 'resolving' },
  failed: { cls: 'bg-red-100 text-red-700', label: 'failed' },
};

function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const e = err as { response?: { data?: { detail?: unknown }; status?: number } };
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (e.response?.status) return `HTTP ${e.response.status}`;
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

const PAGE_SIZE = 50;

export function LinkedinReviewPanel() {
  // The queue runs to hundreds of companies (793 on 2026-09-17); the panel used
  // to fetch a fixed 200 and report that as the total. Page it, and report the
  // queue's real size from the backend's `total`.
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ['linkedin-review-queue', page],
    queryFn: () => listLinkedinReviewQueue({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Rows leave the queue as operators verify them, so a page the operator is
  // sitting on can fall off the end. Snap to the last page rather than showing
  // an empty one under a non-zero total.
  useEffect(() => {
    if (data && page > pageCount) setPage(pageCount);
  }, [data, page, pageCount]);

  if (isLoading) {
    return <div className="text-sm text-grey-500 py-8 text-center">Loading…</div>;
  }
  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded p-3 text-sm text-danger-700">
        Failed to load review queue: {errorMessage(error)}
      </div>
    );
  }
  if (!data || total === 0) {
    return (
      <div className="text-sm text-grey-500 py-12 text-center">
        No LinkedIn tags need review. Companies appear here when the resolver can't
        auto-confirm the org URN, or a covered company has no LinkedIn URL yet.
      </div>
    );
  }

  const first = data.offset + 1;
  const last = data.offset + data.items.length;

  const pager = pageCount > 1 && (
    <div className="flex items-center justify-end gap-3 text-sm text-grey-600">
      <span>Page {page} of {pageCount}</span>
      <button
        className="px-2 py-1 border border-grey-200 rounded disabled:opacity-50"
        disabled={page <= 1}
        onClick={() => setPage(page - 1)}
      >
        Prev
      </button>
      <button
        className="px-2 py-1 border border-grey-200 rounded disabled:opacity-50"
        disabled={page >= pageCount}
        onClick={() => setPage(page + 1)}
      >
        Next
      </button>
    </div>
  );

  return (
    <div className="space-y-3">
      <LinkedInUrnHelper />
      <p className="text-sm text-grey-600">
        {total} compan{total === 1 ? 'y' : 'ies'} need a confirmed LinkedIn
        org URN before their posts can tag them
        {pageCount > 1 ? ` (showing ${first}–${last})` : ''}. Paste/verify the URL and the exact
        <code className="font-mono text-xs"> urn:li:organization:&lt;id&gt;</code>. A wrong
        URN silently tags the wrong company — only verify one you've confirmed.
      </p>
      {pager}
      {data.items.map((item) => (
        <LinkedinReviewRow key={item.entity_id} item={item} />
      ))}
      {pager}
    </div>
  );
}

function LinkedinReviewRow({ item }: { item: LinkedInReviewQueueItem }) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(item.linkedin_url ?? '');
  const [urn, setUrn] = useState(item.linkedin_urn ?? '');
  const [pageName, setPageName] = useState(item.linkedin_page_name ?? '');

  const urnTrimmed = urn.trim();
  const pageNameTrimmed = pageName.trim();
  const urnValid = urnTrimmed === '' || /^urn:li:organization:\d+$/.test(urnTrimmed);
  // Confirming a URN here marks the company verified (taggable). Tagging
  // anchors the page name verbatim in the post, so a URN can't be confirmed
  // without one — otherwise the row looks done but silently never tags.
  const pageNameMissingForUrn = urnTrimmed !== '' && pageNameTrimmed === '';
  // Advisory: does the page name plausibly match THIS company? Soft warning
  // only — the reverse-check proves URN↔page consistency, not that the page is
  // the right company for this entity.
  const nameMismatch = pageNameTrimmed !== '' &&
    !pageNameCorroboratesEntity(pageNameTrimmed, item.display_name, item.canonical_identifier);
  const badge = item.linkedin_resolution_status
    ? STATUS_BADGE[item.linkedin_resolution_status]
    : null;

  const saveMut = useMutation({
    mutationFn: () => updateEntitySocial(item.entity_id, {
      linkedin_url: url.trim() || null,
      linkedin_urn: urnTrimmed || null,
      linkedin_page_name: pageNameTrimmed || null,
      verified: !!urnTrimmed,
      source: 'manual',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedin-review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['entity-social', item.entity_id] });
    },
  });

  return (
    <div className="bg-surface border border-grey-200 rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="font-mono font-semibold text-grey-900 text-sm">
          {item.canonical_identifier || '—'}
        </span>
        <span className="text-sm text-grey-600">{item.display_name}</span>
        {item.priority_group && (
          <span
            title="Sales relationship — these rows sort first, and the resolver re-checks them first"
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
              item.priority_group === 'coverage'
                ? 'bg-primary-100 text-primary-800'
                : item.priority_group === 'campaign'
                  ? 'bg-violet-100 text-violet-800'
                  : 'bg-amber-100 text-amber-800'
            }`}
          >
            {item.priority_group}
          </span>
        )}
        {badge && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${badge.cls}`}>
            {badge.label}
          </span>
        )}
        {item.linkedin_resolution_attempts > 0 && (
          <span className="text-[10px] text-grey-400">
            {item.linkedin_resolution_attempts} attempt{item.linkedin_resolution_attempts === 1 ? '' : 's'}
          </span>
        )}
        {item.linkedin_url && (
          <a
            href={item.linkedin_url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-xs text-primary-700 hover:underline"
          >
            Open candidate ↗
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">Company page URL</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.linkedin.com/company/<slug>"
            className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-grey-600 mb-1">Organisation URN</label>
          <input
            value={urn}
            onChange={(e) => setUrn(e.target.value)}
            placeholder="urn:li:organization:1234567"
            className={
              'w-full border rounded-lg px-3 py-2 text-sm ' +
              (urnValid ? 'border-grey-200' : 'border-red-300')
            }
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-grey-600 mb-1">Page name</label>
          <input
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            placeholder="Exact company name as shown on the LinkedIn page"
            className={
              'w-full border rounded-lg px-3 py-2 text-sm ' +
              (pageNameMissingForUrn ? 'border-red-300' : 'border-grey-200')
            }
          />
          {pageNameMissingForUrn ? (
            <p className="text-xs text-red-600 mt-1">
              Required to confirm a URN — tagging anchors this name verbatim in the post.
            </p>
          ) : (
            <p className="text-xs text-grey-400 mt-1">
              Copy the company's display name from its LinkedIn page header, exactly as shown.
            </p>
          )}
          {nameMismatch && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
              ⚠ This page name doesn't obviously match <b>{item.display_name}</b>
              {item.canonical_identifier ? ` (${item.canonical_identifier})` : ''}. Double-check
              you have the right company's page before confirming.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <p className="text-xs text-grey-400 mr-auto">
          {urnTrimmed
            ? 'Saving with a URN marks it verified — the company will be tagged.'
            : 'Saving a URL only re-queues it for the resolver to find the URN.'}
        </p>
        {saveMut.isError && <span className="text-xs text-red-600">Save failed</span>}
        {saveMut.isSuccess && <span className="text-xs text-green-600">Saved</span>}
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !urnValid || pageNameMissingForUrn || (!url.trim() && !urnTrimmed)}
          className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saveMut.isPending ? 'Saving…' : urnTrimmed ? 'Verify & tag' : 'Save URL'}
        </button>
      </div>
    </div>
  );
}
